import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Users, Camera, ArrowRight, Sparkles } from "lucide-react";

export default function Join() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { register } = useAuth();
  const initialRole = params.get("role") === "vendor" ? "vendor" : "couple";
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem("qld_intended_role", role);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = await register({ email, password, name, role, portfolio_url: portfolio });
      const dest = user.role === "vendor" ? "/dashboard/vendor" : "/dashboard/lovers";
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const isVendor = role === "vendor";

  return (
    <Layout noFooter>
      <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2">
        <div className={`hidden lg:block relative qld-grain ${isVendor ? "bg-sky/30" : "bg-coral/15"}`}>
          <img
            src={isVendor
              ? "https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/rvik2ajn_nicci%20k%20photography-175.jpg"
              : "https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/hd7xm30r_fellipe-ditadi-ASS7bQrIvEk-unsplash.jpg"}
            alt="" className="absolute inset-0 w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-ink/10 to-transparent"/>
          <div className="absolute bottom-12 left-12 right-12 text-cream">
            <p className="qld-overline !text-butter mb-3">{isVendor ? "Welcome, wedding pro" : "Join Love, in Full"}</p>
            <p className="font-serif text-4xl xl:text-5xl leading-tight max-w-md text-cream"
               style={{ fontVariationSettings: '"opsz" 144, "wght" 700, "SOFT" 30, "WONK" 1' }}>
              {isVendor ? "Find lovers from the world that actually exists." : "Be found by creative wedding pros and help shape the industry."}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center p-8 md:p-14">
          <div className="w-full max-w-md">
            <h1 className="font-serif text-4xl md:text-5xl text-wine mb-2"
                style={{ fontVariationSettings: '"opsz" 144, "wght" 700' }}>Create your listing</h1>
            <p className="text-wine/70 mb-8">Choose how you're showing up.</p>

            <div className="grid grid-cols-2 p-1 bg-cream border border-wine/15 rounded-full mb-8" role="tablist">
              <button data-testid="join-role-couple" type="button" onClick={()=>setRole("couple")}
                className={`rounded-full py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-2
                  ${role==="couple" ? "bg-wine text-linen shadow-sm" : "text-wine hover:bg-wine/5"}`}>
                <Users className="w-4 h-4" strokeWidth={1.5}/> Lover
              </button>
              <button data-testid="join-role-vendor" type="button" onClick={()=>setRole("vendor")}
                className={`rounded-full py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-2
                  ${role==="vendor" ? "bg-wine text-linen shadow-sm" : "text-wine hover:bg-wine/5"}`}>
                <Camera className="w-4 h-4" strokeWidth={1.5}/> Wedding Pro
              </button>
            </div>

            {isVendor && (
              <div className="mb-6 p-4 rounded-2xl bg-butter/40 border border-butter text-ink text-sm flex gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5}/>
                <span>Vendor accounts are reviewed manually, to keep everyone safe. You'll be approved within 24 hours and then you can browse the directory.</span>
              </div>
            )}

            <button data-testid="join-google-btn" onClick={handleGoogle}
              className="hidden">
              hidden
            </button>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-wine mb-1.5">
                  {isVendor ? "Business / Brand name" : "Display name"}
                </label>
                <input data-testid="join-name-input" required value={name} onChange={(e)=>setName(e.target.value)}
                  className="lif-input" placeholder={isVendor ? "" : "Sam & Jordan"}/>
              </div>
              {isVendor && (
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">Website</label>
                  <input data-testid="join-portfolio-input" type="url" required value={portfolio} onChange={(e)=>setPortfolio(e.target.value)}
                    className="qld-input" placeholder="https://yourstudio.com"/>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                <input data-testid="join-email-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                  className="qld-input" placeholder="you@example.com"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Password</label>
                <input data-testid="join-password-input" type="password" required minLength={8} value={password}
                  onChange={(e)=>setPassword(e.target.value)} className="qld-input" placeholder="At least 8 characters"/>
              </div>
              {err && <p data-testid="join-error" className="text-destructive text-sm">{err}</p>}
              <button data-testid="join-submit-btn" type="submit" disabled={busy}
                className={isVendor ? "qld-btn-coral w-full" : "qld-btn-primary w-full"}>
                {busy ? "Creating…" : <>Create account <ArrowRight className="w-4 h-4" strokeWidth={1.5}/></>}
              </button>
            </form>

            <p className="text-sm text-muted text-center mt-8">
              Already have an account?{" "}
              <Link to="/login" className="qld-link" data-testid="join-to-login">Log in</Link>
              {" · "}
              <Link to="/forgot-password" className="qld-link" data-testid="join-forgot-link">Forgot password?</Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
